/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components). We had
   `WizardStepper` buried inside EventWizardModal, tied to that wizard's own types; this
   is the general one, and the wizard now builds on it. */
import clsx from 'clsx';
import { BiCheck } from 'react-icons/bi';

import s from './Stepper.module.scss';

/**
 * Where you are in a short flow, and a way back to what you have already done.
 *
 * Two modals had built this by hand — Import statement and Request service —
 * with the same dots, connectors and check marks in two files, and neither was
 * clickable: having answered step 1 you could only reach it again by cancelling
 * out and starting over.
 *
 * A step is a button when it is behind you and plain text when it is ahead:
 * jumping forward past questions the flow has not asked yet is how a wizard
 * ends up with half-filled state, so `furthest` (default: the current step)
 * decides what is reachable rather than every step being live.
 *
 * @alias wizard steps, progress dots, step indicator, breadcrumb of steps
 */
export const Stepper = <T extends string>({
  steps,
  current,
  furthest,
  onStepClick,
  variant = 'dots',
  className,
}: {
  steps: readonly T[];
  /** Index of the step being shown. */
  current: number;
  /** Highest index reachable; defaults to `current`, i.e. back-only. */
  furthest?: number;
  /** Omit to render a read-only indicator. */
  onStepClick?: (index: number) => void;
  /**
   * `dots` names every step on the row — right for a short flow where the
   * labels fit and knowing what is coming is part of deciding to start.
   *
   * `bars` is the wide one: "STEP 1 OF 4" over a row of segments, for a flow
   * whose steps are too many or too long to name in a line, and for a header
   * strip where the indicator has to survive being squeezed. Same rules
   * underneath — a segment is a button when it is behind you.
   */
  variant?: 'dots' | 'bars';
  className?: string;
}) => {
  const reachable = furthest ?? current;

  if (variant === 'bars') {
    return (
      <div className={clsx(s.bars, className)}>
        <span className={s.barsCount}>
          Step {current + 1} of {steps.length}
        </span>
        <ol className={s.barsTrack}>
          {steps.map((label, i) => {
            const done = i <= current;
            const clickable = !!onStepClick && i <= reachable && i !== current;
            return (
              <li key={label} className={s.barsItem}>
                {clickable ? (
                  <button
                    type="button"
                    className={clsx(s.bar, done && s.barDone)}
                    onClick={() => onStepClick(i)}
                    /* The label is the accessible name here: the segments carry
                       no text, so without this a screen reader gets four
                       buttons called nothing. */
                    aria-label={`Step ${i + 1}: ${label}`}
                  />
                ) : (
                  <span
                    className={clsx(s.bar, done && s.barDone)}
                    aria-current={i === current ? 'step' : undefined}
                    aria-label={`Step ${i + 1}: ${label}`}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  return (
    <ol className={clsx(s.steps, className)}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const clickable = !!onStepClick && i <= reachable && !active;
        const Tag = clickable ? 'button' : 'span';

        return (
          <li key={label} className={clsx(s.step, active && s.active, done && s.done)}>
            <Tag
              {...(clickable ? { type: 'button' as const, onClick: () => onStepClick(i) } : {})}
              className={clsx(s.stepInner, clickable && s.clickable)}
              aria-current={active ? 'step' : undefined}
            >
              <span className={s.dot}>{done ? <BiCheck /> : i + 1}</span>
              <span className={s.label}>{label}</span>
            </Tag>
          </li>
        );
      })}
    </ol>
  );
};
